@if (call$ | async; as result) {
  @if (result.isPending) {
    <app-spinner></app-spinner>
  } @else if (result.isError) {
    @if (result.error.error.message; as accessInfo) {
      @if (accessInfo && !accessInfo.hasAccess && accessInfo.sameOrganization) {
        <div class="access-denied-container">
          <h2 translate="CALLS.ACCESS_DENIED_TITLE"></h2>
          <p class="mask-call-name" translate="CALLS.ACCESS_DENIED_MESSAGE" [translateParams]="{ callName: accessInfo.callName }"></p>

          @if (accessInfo.users.length > 0) {
            <div class="user-list-section">
              <h3 translate="CALLS.REQUEST_ACCESS_FROM"></h3>
              <ul class="user-list">
                @for (user of accessInfo.users; track user.id) {
                  <li>
                    <span class="user-name">{{ user.firstName }} {{ user.lastName }}</span>
                    <a href="mailto:{{ user.email }}" class="user-email">{{ user.email }}</a>
                  </li>
                }
              </ul>
              <p class="request-hint" translate="CALLS.REQUEST_ACCESS_HINT"></p>
            </div>
          }
        </div>
      } @else {
        <h2 class="failed-to-load" translate="COMMON.FAILED_TO_LOAD"></h2>
      }
    }
  } @else {
    @if (result.data; as call) {
      <div class="page-container">
        <app-toolbar [title]="call.name" [callId]="call.id" [backButtonLink]="constructBackButtonLink(call)" [showSupportButton]="true">
          <div class="actions">
            @let isRestricted = call.accessLevel === 'RESTRICTED';
            @if (!call.isUpcoming && !call.isOngoing && !isRestricted) {
              <button mat-icon-button [routerLink]="['/app', 'meetings', '' + call.id, 'setup']" [matTooltip]="'CALLS.CALL_SETUP_BUTTON_TOOLTIP' | translate">
                <mat-icon>settings</mat-icon>
              </button>
            }

            @if (call.transcript.length > 0 && !isRestricted) {
              <button mat-icon-button class="export" (click)="exportCall(call)" [matTooltip]="'COMMON.EXPORT' | translate">
                <mat-icon>download</mat-icon>
              </button>
            }

            @if (currentUser$ | async; as user) {
              @if (user.organization.resolvedSettings.isSharingEnabled) {
                <button mat-icon-button class="share" (click)="share(call)" [matTooltip]="'COMMON.SHARING' | translate">
                  <mat-icon>share</mat-icon>
                </button>
              }
              @if (user.role === 'SUPPORT') {
                <button mat-icon-button class="timeline" (click)="showTimeline(call)" [matTooltip]="'CALLS.TIMELINE.BUTTON_TOOLTIP' | translate">
                  <mat-icon fontSet="material-symbols-outlined">schedule</mat-icon>
                </button>
              }
            }
            @if (!call.isUpcoming && !call.isOngoing && !isRestricted) {
              <button mat-icon-button class="info" (click)="toggleInfoPanel()" [matTooltip]="'CALLS.CALL_INFO.TITLE' | translate">
                <mat-icon>info_outline</mat-icon>
              </button>
            }

            @if ((currentUser$ | async)?.isSensitiveModeEnabled) {
              @let isSensitiveModeEnabled = (isSensitiveModeEnabled$ | async) ?? false;
              <button
                mat-icon-button
                class="sensitive-mode-toggle"
                [class.active]="isSensitiveModeEnabled"
                (click)="toggleSensitiveMode()"
                [matTooltip]="(isSensitiveModeEnabled ? 'CALLS.SENSITIVE_MODE.DISABLE' : 'CALLS.SENSITIVE_MODE.ENABLE') | translate"
              >
                <mat-icon>{{ isSensitiveModeEnabled ? 'visibility_off' : 'visibility' }}</mat-icon>
              </button>
            }
          </div>
          <app-call-details-bar class="details" [call]="call" [templateItems]="(templateItems$ | async) ?? []"></app-call-details-bar>
        </app-toolbar>
        @if (call.status === 'WAITING_FOR_RETRY') {
          <app-banner [message]="'CALLS.RESEND_BOT.ON_ITS_WAY' | translate"></app-banner>
        }
        <div class="page">
          <div class="wrapper">
            <mat-drawer-container class="drawer-container">
              <mat-drawer class="sidenav" mode="over" position="end" [opened]="isInfoPanelOpen$ | async" (closed)="callInfo.resetEditState()">
                <app-call-info #callInfo [call]="call"></app-call-info>
                <div class="close-icon">
                  <button mat-icon-button (click)="toggleInfoPanel()">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </mat-drawer>
              @let editorMaximized = (isEditorMaximized$ | async) ?? false;
              <!-- A Restricted user reads only their minutes: no meeting recording, transcript or papers beside them. -->
              @let isMinutesOnly = call.accessLevel === 'RESTRICTED';
              <div class="main-content">
                <as-split [direction]="useVerticalMode ? 'vertical' : 'horizontal'" [gutterSize]="0">
                  <as-split-area [size]="editorMaximized || isMinutesOnly ? 100 : editorSize" [lockSize]="true">
                    <div class="left-side-container">
                      <div class="outputs">
                        <app-call-outputs-container
                          [call]="call"
                          [canUserEdit]="canUserEdit(call)"
                          [canUserSuggest]="canUserSuggest(call)"
                          [canUserComment]="canUserComment(call)"
                          [canResolveAnyThread]="canResolveAnyThread(call)"
                          [isSensitiveModeEnabled]="(isSensitiveModeEnabled$ | async) ?? false"
                          (openAgendaUpload)="callResourcesContainer?.handleAgendaTabClick()"
                          (openDocument)="callResourcesContainer?.handleOpenDocumentEvent($event)"
                          (openMeeting)="callResourcesContainer?.showCitedSentences($event)"
                        ></app-call-outputs-container>
                      </div>
                    </div>
                  </as-split-area>
                  @if (!isMinutesOnly) {
                    <as-split-area [size]="100 - editorSize" [lockSize]="true" [visible]="!editorMaximized">
                      <div class="right-side-container">
                        <app-call-resources-container
                          [canEdit]="canUserEdit(call)"
                          [userHasEditorAccess]="userHasEditorAccess(call)"
                          [call]="call"
                          [isSensitiveModeEnabled]="(isSensitiveModeEnabled$ | async) ?? false"
                        ></app-call-resources-container>
                      </div>
                    </as-split-area>
                  }
                </as-split>
              </div>
            </mat-drawer-container>
          </div>
        </div>
      </div>
    }
  }
}
